import { jsonInit, toSearchParams } from '@shigo-ui/api-client';
import { z } from 'zod';

import { createShigoApiClient, type ShigoApiConfig } from '../../../lib/shigo-api-client';
import {
  blockedCreativeSchema,
  blockedCreativesPageSchema,
  blockedListPublisherPageSchema,
  type BlockedCreativeMutationPayload,
  type BlockedCreativesFilter,
  type BlockedListFileFormat,
  type BlockedListPublisherOption,
} from './model';

interface BlockedListClientOptions {
  config: ShigoApiConfig;
  accessToken: string;
  fetcher?: typeof fetch;
}

const creativesPath = '/api/inventory/block/creatives';
const emptyResponseSchema = z.preprocess((value) => (value === '' ? null : value), z.null());
const uploadResponseSchema = z.union([z.null(), z.string(), z.looseObject({})]);

export function createBlockedListClient({
  config,
  accessToken,
  fetcher,
}: BlockedListClientOptions) {
  const api = createShigoApiClient(config, accessToken, fetcher);

  return {
    list: (filter: BlockedCreativesFilter) =>
      api.request({
        path: `${creativesPath}?${serializeBlockedCreativesFilter(filter)}`,
        schema: blockedCreativesPageSchema,
      }),
    get: (id: string) =>
      api.request({
        path: `${creativesPath}/${encodeURIComponent(id)}`,
        schema: blockedCreativeSchema,
      }),
    create: (payload: BlockedCreativeMutationPayload) =>
      api.request({
        path: creativesPath,
        schema: blockedCreativeSchema,
        init: jsonInit('POST', payload),
      }),
    update: (id: string, payload: BlockedCreativeMutationPayload) =>
      api.request({
        path: `${creativesPath}/${encodeURIComponent(id)}`,
        schema: blockedCreativeSchema,
        init: jsonInit('PUT', payload),
      }),
    delete: (id: string) =>
      api.request({
        path: `${creativesPath}/${encodeURIComponent(id)}`,
        schema: emptyResponseSchema,
        init: { method: 'DELETE' },
      }),
    upload: (format: BlockedListFileFormat, file: File) => {
      const body = new FormData();
      body.append('file', file);
      return api.request({
        path: `${creativesPath}/upload/${format}`,
        schema: uploadResponseSchema,
        init: { method: 'POST', body },
      });
    },
    download: (format: BlockedListFileFormat, searchTerm: string) =>
      api.requestBlob({
        path: `${creativesPath}/download/${format}?${toSearchParams({
          creative: searchTerm.trim() || undefined,
        })}`,
      }),
    listPublishers: () => listAllPublishers(api),
  };
}

export function serializeBlockedCreativesFilter(filter: BlockedCreativesFilter) {
  return toSearchParams({
    page: filter.page,
    size: filter.size,
    creative: filter.searchTerm.trim() || undefined,
    ...(filter.sortProperty
      ? { sortProperty: filter.sortProperty, sortDirection: filter.sortDirection }
      : {}),
  }).toString();
}

async function listAllPublishers(
  api: ReturnType<typeof createShigoApiClient>,
): Promise<BlockedListPublisherOption[]> {
  const pageSize = 100;
  const items: BlockedListPublisherOption[] = [];
  for (let page = 0; ; page += 1) {
    const response = await api.request({
      path: `/api/publishers?${toSearchParams({
        page,
        size: pageSize,
        status: 'ACTIVE',
        sortProperty: 'name',
        sortDirection: 'ASC',
      })}`,
      schema: blockedListPublisherPageSchema,
    });
    items.push(...response.content);
    if (!response.content.length || items.length >= response.totalElements) return items;
  }
}
